/**
 * MapConfigSlide
 *
 * Shows whether the map is configured and links to the
 * map selection dialog to set or edit it.
 */
import React from "react";
import {
  Card,
  CardContent,
  Stack,
  Typography,
  Chip,
  Paper,
  Button,
  Box,
  Divider,
} from "@mui/material";
import MapIcon from "@mui/icons-material/Map";
import DeleteIcon from "@mui/icons-material/Delete";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import AirIcon from "@mui/icons-material/Air";

// Import slide configuration for timing
import { SLIDE_DURATIONS, SLIDE_TYPES } from "../Slides/slideConfig";

/**
 * MapConfigSlide Component
 *
 * Handles the configuration of map display for the kiosk slideshow.
 *
 * @param {Object} props - Component props
 * @param {Object} props.mapConfig - Current map configuration
 * @param {boolean} props.isValidMapConfig - Function to validate map config
 * @param {Function} props.onMapDialogOpen - Map dialog open handler
 * @param {Function} props.onDeleteMap - Map deletion handler
 * @param {Object} props.mapLanguages - Map language configuration
 * @param {boolean} props.showWindBarbMap - Whether to show wind barb map
 * @param {Function} props.onWindBarbMapToggle - Wind barb map toggle handler
 * @param {Object} props.windBarbMapLanguages - Wind barb map language configuration
 * @param {Function} props.onWindBarbMapLanguageChange - Wind barb map language change handler
 *
 * @component
 */
export default function MapConfigSlide({
  mapConfig,
  isValidMapConfig,
  onMapDialogOpen,
  onDeleteMap,
  mapLanguages = { english: true, inuktitut: true },
  showWindBarbMap = false,
  onWindBarbMapToggle,
  windBarbMapLanguages = { english: true, inuktitut: true },
  onWindBarbMapLanguageChange,
}) {
  const isConfigured = isValidMapConfig(mapConfig);
  const mapDuration = SLIDE_DURATIONS[SLIDE_TYPES.MAP] / 1000; // Convert to seconds

  return (
    <Card
      elevation={8}
      sx={{
        border: "2px solid",
        borderColor: isConfigured ? "success.main" : "warning.main",
        borderRadius: 3,
        "&:hover": {
          boxShadow: "0 8px 25px rgba(0,0,0,0.15)",
        },
      }}
    >
      <CardContent sx={{ p: 3 }}>
        {/* Header */}
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
          <MapIcon color="primary" sx={{ fontSize: 28 }} />
          <Typography variant="h6">Slide 2: Map Configuration</Typography>
          <Chip
            label={isConfigured ? "Configured" : "Not Configured"}
            color={isConfigured ? "success" : "warning"}
            size="small"
          />
        </Stack>

        {isConfigured ? (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Map area is configured and will be displayed in the slideshow.
            </Typography>

            {/* Map Details */}
            <Paper sx={{ p: 2, mb: 2, bgcolor: "grey.50" }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Map Configuration Details:
              </Typography>
              <Stack spacing={1}>
                <Typography variant="body2">
                  <strong>Center:</strong>{" "}
                  {mapConfig.center
                    ? `${mapConfig.center[0].toFixed(
                        4
                      )}, ${mapConfig.center[1].toFixed(4)}`
                    : "Not set"}
                </Typography>
                <Typography variant="body2">
                  <strong>Zoom Level:</strong>{" "}
                  {mapConfig.zoom ? mapConfig.zoom.toFixed(1) : "Not set"}
                </Typography>
                {mapConfig.bounds && (
                  <Typography variant="body2">
                    <strong>Bounds:</strong> SW:{" "}
                    {mapConfig.bounds[0][0].toFixed(4)},{" "}
                    {mapConfig.bounds[0][1].toFixed(4)} | NE:{" "}
                    {mapConfig.bounds[1][0].toFixed(4)},{" "}
                    {mapConfig.bounds[1][1].toFixed(4)}
                  </Typography>
                )}
                <Typography variant="body2">
                  <strong>Languages:</strong>{" "}
                  {mapLanguages.english && mapLanguages.inuktitut
                    ? "English, Inuktitut"
                    : mapLanguages.english
                    ? "English"
                    : mapLanguages.inuktitut
                    ? "Inuktitut"
                    : "None selected"}
                </Typography>
              </Stack>
            </Paper>

            {/* Wind Barb Map Status */}
            {showWindBarbMap && (
              <Paper sx={{ p: 2, mb: 2, bgcolor: "success.50" }}>
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{ mb: 1 }}
                >
                  <AirIcon color="success" sx={{ fontSize: 20 }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    Wind Barb Map: Enabled
                  </Typography>
                </Stack>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Languages:</strong>{" "}
                  {windBarbMapLanguages.english &&
                  windBarbMapLanguages.inuktitut
                    ? "English, Inuktitut"
                    : windBarbMapLanguages.english
                    ? "English"
                    : windBarbMapLanguages.inuktitut
                    ? "Inuktitut"
                    : "None selected"}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Wind barb map uses the same map area as configured above.
                </Typography>
              </Paper>
            )}

            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                startIcon={<MapIcon />}
                onClick={onMapDialogOpen}
                size="small"
              >
                Edit Map Area
              </Button>
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={onDeleteMap}
                size="small"
              >
                Delete Map
              </Button>
            </Stack>
          </Box>
        ) : (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              No map area configured. Click below to select a map area for
              display.
            </Typography>
            <Button
              variant="contained"
              startIcon={<MapIcon />}
              onClick={onMapDialogOpen}
              size="small"
            >
              Configure Map Area
            </Button>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
